import { useState, Fragment, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Loader2, Download, Sparkles, Search, X, Package } from "lucide-react";
import { CutoutPreview } from "./components/CutoutPreview";
import { Toaster, toast } from "sonner";
import ProductRow from "./components/ProductRow";
import VariantAccordion from "./components/VariantAccordion";
import LockScreen from "./components/LockScreen";
import FlipCounter from "./components/FlipCounter";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./components/ui/accordion";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [shopName, setShopName] = useState(() => localStorage.getItem("surfify_shop") || "");
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [collectionUrl, setCollectionUrl] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rewritingIndex, setRewritingIndex] = useState(null);
  const [rewritingAll, setRewritingAll] = useState(false);
  const [rewriteAllProgress, setRewriteAllProgress] = useState({ current: 0, total: 0 });
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [processingImages, setProcessingImages] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [filterText, setFilterText] = useState("");
  const [stats, setStats] = useState({ descriptions_generated: 0, images_downloaded: 0 });
  const [recentUrls, setRecentUrls] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
    } catch (e) { /* silent */ }
  }, []);

  const fetchRecentUrls = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/recent-urls`);
      setRecentUrls(res.data.recent_urls || []);
    } catch (e) { /* silent */ }
  }, []);

  // Load stats + recent URLs immediately on mount — public endpoints, no auth required
  useEffect(() => {
    fetchStats();
    fetchRecentUrls();
    const interval = setInterval(() => {
      fetchStats();
      fetchRecentUrls();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchRecentUrls]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.post(`${API}/scrape-collection`, {
        collection_url: "test",
        brand_name: "test"
      });
      setIsAuthenticated(true);
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
      } else {
        setIsAuthenticated(true);
      }
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLoginSuccess = (name, shop) => {
    setFirstName(name);
    setShopName(shop);
    localStorage.setItem("surfify_shop", shop);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/logout`);
      setIsAuthenticated(false);
      setFirstName("");
      setShopName("");
      localStorage.removeItem("surfify_shop");
      setProducts([]);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Axios interceptor to handle 401 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          setIsAuthenticated(false);
          setFirstName("");
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const handleScrape = async () => {
    if (!collectionUrl.trim()) {
      toast.error("Please enter a collection URL");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/scrape-collection`, {
        collection_url: collectionUrl,
        brand_name: shopName,
      });

      if (response.data.success) {
        setProducts(response.data.products);
        toast.success(`Loaded ${response.data.count} products`);
        fetchRecentUrls();  // refresh the global recent-URLs feed
      }
    } catch (error) {
      console.error("Error scraping collection:", error);
      toast.error(error.response?.data?.detail || "Failed to load collection");
    } finally {
      setLoading(false);
    }
  };

  const handleRewrite = async (index) => {
    const product = products[index];
    setRewritingIndex(index);

    try {
      const originalBrand = product.vendor || extractBrandFromUrl(collectionUrl);
      // Increment a per-product rewrite count so each click forces a new variation.
      // Seed = current ms + count guarantees uniqueness across clicks AND across products.
      const nextCount = (product.rewrite_count || 0) + 1;
      const response = await axios.post(`${API}/rewrite-description`, {
        original_description: product.original_description || `Product: ${product.title}`,
        product_title: product.title,
        brand_name: shopName,
        original_brand: originalBrand,
        product_url: product.product_url,
        variation_seed: Date.now() + index * 1000 + nextCount,
        previous_variation: product.variation || null,
      });

      if (response.data.success) {
        const updatedProducts = [...products];
        updatedProducts[index].rewritten_description = response.data.rewritten_description;
        updatedProducts[index].had_specs = response.data.had_specs;
        updatedProducts[index].variation = response.data.variation;
        updatedProducts[index].rewrite_count = nextCount;
        updatedProducts[index].mode = response.data.mode || 'full';
        setProducts(updatedProducts);
        const message = response.data.had_specs
          ? "Description rewritten with specs included"
          : "Description rewritten successfully";
        toast.success(message);
        setStats(prev => ({ ...prev, descriptions_generated: prev.descriptions_generated + 1 }));
      }
    } catch (error) {
      console.error("Error rewriting description:", error);
      toast.error(error.response?.data?.detail || "Failed to rewrite description");
    } finally {
      setRewritingIndex(null);
    }
  };

  const handleCondense = async (index) => {
    const product = products[index];
    setRewritingIndex(index);

    try {
      const originalBrand = product.vendor || extractBrandFromUrl(collectionUrl);
      const response = await axios.post(`${API}/rewrite-description`, {
        original_description: product.original_description || `Product: ${product.title}`,
        product_title: product.title,
        brand_name: shopName,
        original_brand: originalBrand,
        product_url: product.product_url,
        mode: 'condensed',
      });

      if (response.data.success) {
        const updatedProducts = [...products];
        updatedProducts[index].rewritten_description = response.data.rewritten_description;
        updatedProducts[index].had_specs = response.data.had_specs;
        updatedProducts[index].variation = response.data.variation;
        updatedProducts[index].mode = 'condensed';
        setProducts(updatedProducts);
        toast.success("Condensed description generated");
        setStats(prev => ({ ...prev, descriptions_generated: prev.descriptions_generated + 1 }));
      }
    } catch (error) {
      console.error("Error condensing description:", error);
      toast.error(error.response?.data?.detail || "Failed to condense description");
    } finally {
      setRewritingIndex(null);
    }
  };

  const handleRewriteAll = async () => {
    const pendingIndices = products
      .map((p, i) => i)
      .filter((i) => !products[i].rewritten_description);

    if (pendingIndices.length === 0) {
      toast.info("All products already have rewritten descriptions");
      return;
    }

    setRewritingAll(true);
    setRewriteAllProgress({ current: 0, total: pendingIndices.length });

    let currentProducts = [...products];
    let lastVariation = null; // track last batch variation to discourage in-batch repeats

    for (let i = 0; i < pendingIndices.length; i++) {
      const index = pendingIndices[i];
      const product = currentProducts[index];
      setRewriteAllProgress({ current: i + 1, total: pendingIndices.length });
      setRewritingIndex(index);

      try {
        const originalBrand = product.vendor || extractBrandFromUrl(collectionUrl);
        const response = await axios.post(`${API}/rewrite-description`, {
          original_description: product.original_description || `Product: ${product.title}`,
          product_title: product.title,
          brand_name: shopName,
          original_brand: originalBrand,
          product_url: product.product_url,
          variation_seed: Date.now() + index * 1000 + i,
          previous_variation: lastVariation,
        });

        if (response.data.success) {
          currentProducts = [...currentProducts];
          currentProducts[index] = {
            ...currentProducts[index],
            rewritten_description: response.data.rewritten_description,
            had_specs: response.data.had_specs,
            variation: response.data.variation,
            rewrite_count: 1,
          };
          lastVariation = response.data.variation;
          setProducts(currentProducts);
          setStats(prev => ({ ...prev, descriptions_generated: prev.descriptions_generated + 1 }));
        }
      } catch (error) {
        console.error(`Error rewriting product ${index}:`, error);
        toast.error(`Failed to rewrite: ${product.title}`);
      }
    }

    setRewritingIndex(null);
    setRewritingAll(false);
    setRewriteAllProgress({ current: 0, total: 0 });
    toast.success(`Rewrote ${pendingIndices.length} descriptions`);
  };

  const [preview, setPreview] = useState(null);

  const openPreview = async (index, mode = "white") => {
    const product = products[index];
    if ((product.images?.length || 0) === 0) {
      toast.error("No images available for this product");
      return;
    }

    setPreview({ index, mode, loading: true, previews: [] });
    try {
      const response = await axios.post(`${API}/preview-cutouts`, {
        image_urls: product.images,
        background: mode
      }, { timeout: 180000 });
      setPreview({ index, mode, loading: false, previews: response.data.previews });
    } catch (error) {
      console.error("Preview failed:", error);
      const isTimeout = error?.code === 'ECONNABORTED' || (error?.message || '').toLowerCase().includes('timeout');
      toast.error(isTimeout
        ? "Preview took too long — try again in a few seconds (server is warming up)"
        : `Could not build previews (${error?.response?.status || 'network error'})`);
      setPreview(null);
    }
  };

  const openVariantPreview = async (productIndex, variant, mode = "white") => {
    const images = variant?.images || [];
    if (images.length === 0) {
      toast.error(`No images for ${variant.title}`);
      return;
    }
    setPreview({ index: productIndex, mode, loading: true, previews: [], variant });
    try {
      const response = await axios.post(`${API}/preview-cutouts`, {
        image_urls: images,
        background: mode
      }, { timeout: 180000 });
      setPreview({ index: productIndex, mode, loading: false, previews: response.data.previews, variant });
    } catch (error) {
      console.error("Variant preview failed:", error);
      const isTimeout = error?.code === 'ECONNABORTED' || (error?.message || '').toLowerCase().includes('timeout');
      toast.error(isTimeout
        ? "Preview took too long — try again in a few seconds"
        : `Could not build previews (${error?.response?.status || 'network error'})`);
      setPreview(null);
    }
  };

  // Trigger a browser download from an axios blob response. Returns delivery info.
  const saveBlobResponse = (response, fallback) => {
    const contentType = response.headers['content-type'] || '';
    const disposition = response.headers['content-disposition'] || '';
    const nameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = nameMatch ? decodeURIComponent(nameMatch[1]) : fallback;
    const url = window.URL.createObjectURL(new Blob([response.data], contentType ? { type: contentType } : undefined));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return {
      filename,
      requested: Number(response.headers['x-images-requested'] || 1),
      delivered: Number(response.headers['x-images-delivered'] || 1),
    };
  };

  // 2-9 images download as individual loose files (one request per image with
  // only_index); 10+ images fall back to a single streaming ZIP. Returns the
  // number of images actually delivered.
  const LOOSE_FILE_MAX = 9;
  const downloadLooseFiles = async (payload, imageCount) => {
    let delivered = 0;
    for (let i = 1; i <= imageCount; i++) {
      try {
        const response = await axios.post(
          `${API}/download-product-images`,
          { ...payload, only_index: i },
          { responseType: 'blob' }
        );
        saveBlobResponse(response, `${payload.vendor || 'product'}_${payload.handle}_${i}.jpg`);
        delivered += 1;
        // Small gap so browsers don't suppress rapid successive downloads.
        await new Promise((r) => setTimeout(r, 400));
      } catch (error) {
        console.error(`Loose download failed for image ${i}:`, error);
      }
    }
    return delivered;
  };

  const handleDownloadImages = async (index, background = "") => {
    const product = products[index];
    const imageCount = product.images?.length || 0;
    
    if (imageCount === 0) {
      toast.error("No images available for this product");
      return;
    }

    if (background) {
      setProcessingImages({ index, background });
      toast.info(background === "white"
        ? "Rendering on white — this can take a moment per image"
        : "Removing backgrounds — this can take a moment per image");
    } else {
      setDownloadingIndex(index);
    }
    
    try {
      const payload = {
        shop_name: shopName,
        vendor: product.vendor || 'product',
        handle: product.handle,
        image_urls: product.images,
        variants: product.variants || [],
        background,
      };

      // 2-9 images → loose individual files; 1 or 10+ → single response (raw / ZIP).
      if (imageCount >= 2 && imageCount <= LOOSE_FILE_MAX) {
        const delivered = await downloadLooseFiles(payload, imageCount);
        if (delivered === 0) {
          toast.error("Failed to download images");
        } else if (delivered < imageCount) {
          toast.warning(`Downloaded ${delivered} of ${imageCount} images — the store dropped the rest`);
        } else {
          toast.success(`Downloaded ${delivered} images`);
        }
        setStats(prev => ({ ...prev, images_downloaded: prev.images_downloaded + delivered }));
        return;
      }

      const response = await axios.post(
        `${API}/download-product-images`,
        payload,
        { responseType: 'blob' }
      );

      const contentType = response.headers['content-type'] || '';
      const isZip = contentType.includes('zip');
      const fallback = `${product.vendor || 'product'}_${product.handle}${isZip ? `_images${background ? `_${background}` : ''}.zip` : '.jpg'}`;
      const { filename, requested, delivered } = saveBlobResponse(response, fallback);

      if (delivered < requested) {
        toast.warning(`Downloaded ${delivered} of ${requested} images — the store dropped the rest`);
      } else {
        toast.success(delivered === 1 ? `Downloaded ${filename}` : `Downloaded ${delivered} images`);
      }
      setStats(prev => ({ ...prev, images_downloaded: prev.images_downloaded + delivered }));
    } catch (error) {
      console.error("Error downloading images:", error);
      toast.error("Failed to download images");
    } finally {
      setDownloadingIndex(null);
      setProcessingImages(null);
    }
  };

  const handleDownloadVariantImages = async (productIndex, variant, background = "") => {
    const product = products[productIndex];
    const images = variant?.images || [];
    if (images.length === 0) {
      toast.error(`No images for ${variant.title}`);
      return;
    }
    const variantSlug = (variant.title || 'variant').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'variant';

    if (background) {
      setProcessingImages({ index: productIndex, background, variantId: variant.variant_id });
      toast.info(background === "white"
        ? `Rendering ${variant.title} on white — this can take a moment`
        : `Cutting ${variant.title} to transparent — this can take a moment`);
    } else {
      setDownloadingIndex(`${productIndex}:${variant.variant_id}`);
    }

    try {
      const payload = {
        shop_name: shopName,
        vendor: product.vendor || 'product',
        handle: `${product.handle}-${variantSlug}`,
        image_urls: images,
        variants: [variant],
        background,
      };

      if (images.length >= 2 && images.length <= LOOSE_FILE_MAX) {
        const delivered = await downloadLooseFiles(payload, images.length);
        if (delivered === 0) {
          toast.error(`Failed to download ${variant.title} images`);
        } else if (delivered < images.length) {
          toast.warning(`Downloaded ${delivered} of ${images.length} ${variant.title} images`);
        } else {
          toast.success(`Downloaded ${delivered} ${variant.title} images`);
        }
        setStats(prev => ({ ...prev, images_downloaded: prev.images_downloaded + delivered }));
        return;
      }

      const response = await axios.post(
        `${API}/download-product-images`,
        payload,
        { responseType: 'blob' }
      );

      const contentType = response.headers['content-type'] || '';
      const isZip = contentType.includes('zip');
      const fallback = `${product.vendor || 'product'}_${product.handle}_${variantSlug}${isZip ? `${background ? `_${background}` : ''}.zip` : '.jpg'}`;
      const { filename, delivered } = saveBlobResponse(response, fallback);

      toast.success(delivered === 1 ? `Downloaded ${filename}` : `Downloaded ${delivered} ${variant.title} images`);
      setStats(prev => ({ ...prev, images_downloaded: prev.images_downloaded + delivered }));
    } catch (error) {
      console.error(`Error downloading ${variant.title} images:`, error);
      toast.error(`Failed to download ${variant.title} images`);
    } finally {
      setDownloadingIndex(null);
      setProcessingImages(null);
    }
  };

  const visibleProducts = products
    .map((product, originalIndex) => ({ product, originalIndex }))
    .filter(({ product }) => {
      if (!filterText) return true;
      const needle = filterText.toLowerCase();
      return [product.title, product.handle, product.vendor]
        .some((field) => (field || "").toLowerCase().includes(needle));
    });

  const extractBrandFromUrl = (url) => {    try {
      const hostname = new URL(url).hostname;
      const parts = hostname.split('.');
      const brand = parts[0];
      return brand.charAt(0).toUpperCase() + brand.slice(1);
    } catch {
      return "the brand";
    }
  };

  const handleCopy = async (text, index) => {
    if (!text) {
      toast.error("No description to copy");
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopiedIndex(null), 2000);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        textArea.remove();
        
        if (successful) {
          setCopiedIndex(index);
          toast.success("Copied to clipboard");
          setTimeout(() => setCopiedIndex(null), 2000);
        } else {
          throw new Error("Copy command failed");
        }
      }
    } catch (error) {
      console.error("Copy failed:", error);
      prompt("Copy this text manually:", text);
    }
  };

  const handleExport = async () => {
    if (products.length === 0) {
      toast.error("No products to export");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/export-csv`,
        { products },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      const filename = `products_${Date.now()}.csv`;
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported successfully");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    }
  };

  const toggleProductExpand = (index) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedProducts(newExpanded);
  };

  const handleVendorEdit = (index, newVendor) => {
    const updated = [...products];
    updated[index] = { ...updated[index], vendor: newVendor };
    setProducts(updated);
  };

  // Show lock screen if not authenticated
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#39FF14] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LockScreen onLoginSuccess={handleLoginSuccess} stats={stats} />;
  }

  return (
    <div className="min-h-screen bg-[#050505] noise-texture">
      <Toaster position="top-right" theme="dark" toastOptions={{
        style: {
          background: '#0A0A0A',
          border: '1px solid #00FFFF',
          color: '#FFFFFF',
        },
      }} />
      
      <div className="max-w-7xl mx-auto p-6 md:p-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-end gap-2">
              <h1
                className="font-wordmark uppercase bg-clip-text text-transparent leading-[0.95] select-none"
                style={{
                  fontSize: 'clamp(3.5rem, 8vw, 6rem)',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                  backgroundImage: 'linear-gradient(to right, #2DD4FF 0%, #5B8BFF 35%, #9461FF 70%, #D63DEC 100%)',
                }}
                data-testid="surfify-wordmark-header"
              >
                SURFIFY
              </h1>
              <Package
                className="text-[#D63DEC] mb-[0.18em] shrink-0"
                strokeWidth={2}
                style={{ width: 'clamp(0.75rem, 1.5vw, 1.125rem)', height: 'clamp(0.75rem, 1.5vw, 1.125rem)' }}
                data-testid="surfify-wordmark-dot-header"
              />
              {shopName && (
                <span className="text-zinc-400 font-mono text-base md:text-lg tracking-wide self-baseline ml-2" data-testid="shop-name-header">
                  for {shopName}.
                </span>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="text-zinc-500 hover:text-zinc-300 text-sm font-mono transition-colors"
            >
              Logout
            </button>
          </div>
          <p className="text-zinc-400 text-xs font-mono uppercase tracking-wider">Rich, customised product descriptions and SKU-named image files in just a few clicks.</p>
          <div className="flex items-center gap-3 mt-2 text-xs font-mono text-zinc-500 flex-wrap" data-testid="live-stats">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FFFF] animate-pulse inline-block"></span>
              You legends have saved time with:
            </span>
            <span className="flex items-center gap-1.5">
              <FlipCounter target={stats.descriptions_generated} delay={400} />
              <span className="text-zinc-600">product descriptions generated and</span>
              <FlipCounter target={stats.images_downloaded} delay={700} />
              <span className="text-zinc-600">images downloaded.</span>
            </span>
          </div>
          <div className="mt-3 max-w-md">
            <Accordion type="single" collapsible className="border border-[#27272A] bg-[#0A0A0A]/60">
              <AccordionItem value="value-stack" className="border-b-0">
                <AccordionTrigger
                  className="px-4 py-3 text-xs font-mono uppercase tracking-wider text-zinc-300 hover:no-underline hover:text-white"
                  data-testid="value-stack-trigger-header"
                >
                  The Value Stack
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-5 pt-1" data-testid="value-stack-content-header">
                  <div className="space-y-4 text-xs font-mono leading-relaxed">
                    <div>
                      <p className="text-[#00FFFF] uppercase tracking-wider mb-1">Save Time</p>
                      <p className="text-zinc-300 font-bold">
                        Re-writing, tweaking and customising product descriptions for every new arrival takes time as does downloading images. Surfify does it in a few clicks.
                      </p>
                    </div>
                    <div>
                      <p className="text-[#00FFFF] uppercase tracking-wider mb-1">Discoverable 24/7</p>
                      <p className="text-zinc-300 font-bold">
                        Rich descriptions and smart file names means search engines, AI bots and Google Shopping are getting the right signals, around the clock.
                      </p>
                    </div>
                    <div>
                      <p className="text-[#00FFFF] uppercase tracking-wider mb-1">Customer Experience</p>
                      <p className="text-zinc-300 font-bold">
                        We've all landed on a product page with one image and three bullet points. Feels like no-one's home. Customers will see and feel the difference.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Personalized Greeting */}
        {firstName && (
          <div className="mb-8">
            <p className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
              Hey {firstName} — let's save you some time.
            </p>
          </div>
        )}

        {/* Command Center */}
        <div className="bg-[#0A0A0A] border border-[#27272A] p-8 mb-8 hover:border-[#00FFFF]/40 transition-all" data-testid="command-center">
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">
                COLLECTION OR PRODUCT URL
              </label>
              <input
                type="text"
                value={collectionUrl}
                onChange={(e) => setCollectionUrl(e.target.value)}
                placeholder="https://store.myshopify.com/collections/all"
                className="w-full bg-transparent border-b-2 border-[#333333] text-lg text-white placeholder:text-zinc-700 focus:border-[#00FFFF] focus:outline-none h-12 px-0 font-mono transition-all"
                data-testid="collection-url-input"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                onClick={handleScrape}
                disabled={loading}
                className="bg-[#00FFFF] text-black hover:bg-[#00CCDD] font-bold uppercase tracking-wider px-8 py-3 transition-all hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="fetch-products-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    LOADING...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    FETCH PRODUCTS
                  </>
                )}
              </button>
              
              {products.length > 0 && (
                <>
                  <button
                    onClick={handleRewriteAll}
                    disabled={rewritingAll || loading}
                    className="bg-transparent border border-[#00FFFF] text-[#00FFFF] hover:bg-[#00FFFF]/10 uppercase tracking-wider px-8 py-3 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="rewrite-all-btn"
                  >
                    {rewritingAll ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        REWRITING {rewriteAllProgress.current}/{rewriteAllProgress.total}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        REWRITE ALL
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleExport}
                    className="bg-transparent border border-[#333333] text-white hover:border-[#FF00CC] hover:text-[#FF00CC] uppercase tracking-wider px-8 py-3 transition-all flex items-center gap-2"
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-5 h-5" />
                    EXPORT CSV
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Products Table */}
        {products.length > 0 && (() => {
          const filteredProducts = visibleProducts;

          return (
            <div className="bg-[#0A0A0A] border border-[#27272A] overflow-hidden" data-testid="products-table">
              {/* Filter bar */}
              <div className="flex items-center gap-4 px-4 py-3 border-b border-[#27272A]">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                  <input
                    type="text"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    placeholder="Filter products..."
                    className="w-full bg-transparent border-b border-[#333333] text-sm text-white placeholder:text-zinc-600 focus:border-[#00FFFF] focus:outline-none py-1 pl-6 font-mono transition-all"
                    data-testid="filter-input"
                  />
                  {filterText && (
                    <button
                      onClick={() => setFilterText("")}
                      className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                      data-testid="clear-filter-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="text-xs font-mono text-zinc-500" data-testid="product-count">
                  {filterText
                    ? `${filteredProducts.length} of ${products.length} products`
                    : `${products.length} product${products.length !== 1 ? 's' : ''}`}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#111111] border-b border-[#27272A]">
                      <th className="text-left p-4 text-xs font-mono uppercase tracking-wider text-zinc-400 w-12"></th>
                      <th className="text-left p-4 text-xs font-mono uppercase tracking-wider text-zinc-400 w-24">IMAGE</th>
                      <th className="text-left p-4 text-xs font-mono uppercase tracking-wider text-zinc-400">PRODUCT</th>
                      <th className="text-left p-4 text-xs font-mono uppercase tracking-wider text-zinc-400">HANDLE</th>
                      <th className="text-left p-4 text-xs font-mono uppercase tracking-wider text-zinc-400 w-[400px]">DESCRIPTION</th>
                      <th className="text-left p-4 text-xs font-mono uppercase tracking-wider text-zinc-400 w-48">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.length > 0 ? filteredProducts.map(({ product, originalIndex }) => {
                      const productKey = product.id || product.handle || `product-${originalIndex}`;
                      const isExpanded = expandedProducts.has(originalIndex);
                      const isRewriting = rewritingIndex === originalIndex;
                      const isDownloading = downloadingIndex === originalIndex;
                      const isDownloadingTransparent = processingImages?.index === originalIndex && processingImages?.background === "transparent";
                      const isDownloadingWhite = processingImages?.index === originalIndex && processingImages?.background === "white";
                      const isCopied = copiedIndex === originalIndex;

                      return (
                        <Fragment key={productKey}>
                          <ProductRow
                            product={product}
                            index={originalIndex}
                            isExpanded={isExpanded}
                            onToggleExpand={() => toggleProductExpand(originalIndex)}
                            onRewrite={() => handleRewrite(originalIndex)}
                            onCondense={() => handleCondense(originalIndex)}
                            onDownloadImages={() => handleDownloadImages(originalIndex)}
                            onDownloadTransparentImages={() => handleDownloadImages(originalIndex, "transparent")}
                            onDownloadWhiteImages={() => handleDownloadImages(originalIndex, "white")}
                            onPreviewCutouts={() => openPreview(originalIndex)}
                            onCopy={() => handleCopy(product.rewritten_description, originalIndex)}
                            onVendorEdit={(newVendor) => handleVendorEdit(originalIndex, newVendor)}
                            isRewriting={isRewriting}
                            isDownloading={isDownloading}
                            isDownloadingTransparent={isDownloadingTransparent}
                            isDownloadingWhite={isDownloadingWhite}
                            isCopied={isCopied}
                          />
                          {isExpanded && (
                            <VariantAccordion
                              variants={product.variants}
                              productIndex={originalIndex}
                              processingVariantId={processingImages?.index === originalIndex ? processingImages?.variantId : null}
                              processingBackground={processingImages?.background}
                              onPreviewVariant={(variant, mode) => openVariantPreview(originalIndex, variant, mode)}
                              onDownloadVariant={(variant, mode) => handleDownloadVariantImages(originalIndex, variant, mode)}
                            />
                          )}
                        </Fragment>
                      );
                    }) : (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-zinc-600 font-mono text-sm">
                          No products match &quot;{filterText}&quot;
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Empty State */}
        {products.length === 0 && !loading && (
          <div className="text-center py-20" data-testid="empty-state">
            <div className="text-zinc-600 font-mono text-sm uppercase tracking-wider mb-4">
              NO DATA LOADED
            </div>
            <div className="text-zinc-500 text-xs">
              Enter a Shopify collection URL above to get started
            </div>
          </div>
        )}

        {/* Global Recently Fetched URLs — last 10 across all users */}
        {recentUrls.length > 0 && (
          <div className="mt-16 pt-8 border-t border-[#1F1F1F]" data-testid="recent-urls">
            <div className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-3">
              Recently Fetched Products
            </div>
            <ul className="space-y-1.5">
              {recentUrls.map((entry, i) => (
                <li
                  key={`${entry.url}-${entry.at}-${i}`}
                  className="text-xs font-mono text-zinc-600 flex items-center gap-2 truncate"
                  data-testid={`recent-url-${i}`}
                >
                  <span className="text-zinc-700 shrink-0">{String(i + 1).padStart(2, '0')}.</span>
                  <a
                    href={entry.url}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate hover:text-[#00FFFF] transition-colors"
                    title={entry.url}
                  >
                    {entry.url}
                  </a>
                  {typeof entry.count === 'number' && (
                    <span className="text-zinc-700 shrink-0">· {entry.count} products</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {preview && (
        <CutoutPreview
          product={preview.variant
            ? { ...products[preview.index], title: `${products[preview.index].title} — ${preview.variant.title}` }
            : products[preview.index]}
          previews={preview.previews}
          isLoading={preview.loading}
          imageCount={preview.variant
            ? (preview.variant.images?.length || 0)
            : (products[preview.index]?.images?.length || 0)}
          mode={preview.mode}
          onModeChange={(mode) => preview.variant
            ? openVariantPreview(preview.index, preview.variant, mode)
            : openPreview(preview.index, mode)}
          onClose={() => setPreview(null)}
          onDownload={(mode) => preview.variant
            ? handleDownloadVariantImages(preview.index, preview.variant, mode)
            : handleDownloadImages(preview.index, mode)}
          isDownloading={processingImages?.index === preview.index}
        />
      )}
    </div>
  );
}

export default App;
